import React, { Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { notifyRouteChange } from "@/utils/versionCheck";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { EngineProvider } from "@/contexts/EngineContext";
import { InstallProvider } from "@/contexts/InstallContext";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { RefreshCw } from "lucide-react";
import { isAndroidWebView, isInAppBrowser } from "@/utils/webviewDetect";

// Detect ChunkLoadError (stale webpack bundle) vs regular app errors.
// ChunkLoadError names: "ChunkLoadError" (webpack 4/5) or message contains
// "Loading chunk" / "Loading CSS chunk" / "Failed to fetch dynamically imported module".
function isChunkLoadError(err) {
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  const msg = (err.message || '') + (err.name || '');
  return /loading chunk|loading css chunk|failed to fetch dynamically imported module/i.test(msg);
}

// Session-storage key used as a one-shot reload guard.
const CHUNK_RELOAD_KEY = 'bm_chunk_reload_attempted';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(err) {
    return { hasError: true, isChunkError: isChunkLoadError(err) };
  }

  componentDidCatch(err, info) {
    if (isChunkLoadError(err)) {
      console.warn('[ErrorBoundary] ChunkLoadError detected:', err.message);
      // Auto-reload once to pick up the new build.
      // sessionStorage guards against an infinite reload loop.
      try {
        if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
          console.info('[ErrorBoundary] Auto-reloading to recover stale chunk...');
          window.location.reload();
          return; // reload is in-flight — nothing more to do
        }
        // Second failure after reload: clear the flag so a future session can
        // try again, but do NOT reload infinitely.
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        console.error('[ErrorBoundary] ChunkLoadError persists after reload — showing manual error.');
      } catch {
        // sessionStorage unavailable (private mode etc.) — fall through to UI
      }
    } else {
      console.error('[ErrorBoundary]', err, info);
    }
  }

  render() {
    if (this.state.hasError) {
      const { isChunkError } = this.state;
      return (
        <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
          <div className="text-center space-y-4 max-w-xs">
            <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mx-auto">
              <RefreshCw size={24} className="text-white/60" />
            </div>
            <h2 className="font-heading font-black text-xl uppercase tracking-tight text-white">
              {isChunkError ? 'App Updated' : 'Something Went Wrong'}
            </h2>
            <p className="text-sm text-white/50">
              {isChunkError
                ? 'A new version is available. Tap below to load it.'
                : 'An unexpected error occurred. Tap below to reload.'}
            </p>
            <button
              onClick={() => {
                try { sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch {}
                this.setState({ hasError: false, isChunkError: false });
                window.location.reload();
              }}
              className="w-full py-3 rounded-xl bg-accent text-accent-foreground font-heading font-bold text-sm uppercase tracking-wider active:scale-95 transition-transform"
              data-testid="error-boundary-reload"
            >
              {isChunkError ? 'Load Latest Version' : 'Reload App'}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

import { PageTransition } from "@/components/PageTransition";

// Critical path — loaded eagerly
import Landing from "@/pages/Landing";
const SimpleLanding = React.lazy(() => import("@/pages/SimpleLanding"));
const AppWelcome = React.lazy(() => import("@/pages/AppWelcome"));
import Auth from "@/pages/Auth";
import GoogleCallback from "@/pages/GoogleCallback";
import Dashboard from "@/pages/Dashboard";
import Workout from "@/pages/Workout";

// Core pages — lazy with fast fallback (no spinner, renders cached data first)
const Chat = React.lazy(() => import("@/pages/Chat"));
const Achievements = React.lazy(() => import("@/pages/Achievements"));
const Settings = React.lazy(() => import("@/pages/Settings"));
const Onboarding = React.lazy(() => import("@/pages/Onboarding"));
const ActiveWorkout = React.lazy(() => import("@/pages/ActiveWorkout"));
const CustomProgramBuilder = React.lazy(() => import("@/pages/CustomProgramBuilder"));
const SubscriptionPage = React.lazy(() => import("@/pages/Subscription").then(m => ({ default: m.default })));
const SubscriptionSuccess = React.lazy(() => import("@/pages/Subscription").then(m => ({ default: m.SubscriptionSuccess })));
const SubscriptionCancel = React.lazy(() => import("@/pages/Subscription").then(m => ({ default: m.SubscriptionCancel })));

// Rarely visited — lazy loaded
const Analytics = React.lazy(() => import("@/pages/Analytics"));
const FAQs = React.lazy(() => import("@/pages/FAQs"));
// Rarely visited — lazy loaded
const Support = React.lazy(() => import("@/pages/Support"));
const PrivacyPolicy = React.lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = React.lazy(() => import("@/pages/TermsOfService"));
const DeleteAccount = React.lazy(() => import("@/pages/DeleteAccount"));
const DevExerciseGallery = React.lazy(() => import("@/pages/DevExerciseGallery"));
const ExerciseLibrary = React.lazy(() => import("@/pages/ExerciseLibrary"));
const Admin = React.lazy(() => import("@/pages/Admin"));
const ExerciseAudit = React.lazy(() => import("@/pages/ExerciseAudit"));
const Progression = React.lazy(() => import("@/pages/Progression"));
const ForgotPassword = React.lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("@/pages/ResetPassword"));
const ConfirmEmailChange = React.lazy(() => import("@/pages/ConfirmEmailChange"));
const BlogPage = React.lazy(() => import("@/pages/BlogPage"));
const BlogArticle = React.lazy(() => import("@/pages/BlogArticle"));


import { Toaster } from "@/components/ui/sonner";
import { CookieConsent } from "@/components/CookieConsent";
import { AppLayout } from "@/components/AppLayout";
import { InAppBrowserBanner } from "@/components/InAppBrowserBanner";
import { usePageTracking } from "@/hooks/usePageTracking";
import axios from 'axios';

function AccountDeletionRedirect() {
  useEffect(() => { window.location.href = '/account-deletion.html'; }, []);
  return null;
}

function HomeRoute() {
  const { user, loading, token } = useAuth();
  const [searchParams] = useSearchParams();

  // Detect installed app environment using multiple signals.
  // NOTE: webtonative.min.js is loaded for EVERY visitor in public/index.html, so
  // `!!window.WTN` is always true and must never be used as the app signal. Ask the
  // library itself whether we're running inside the native shell.
  const isWtnApp = typeof window !== 'undefined' && (() => {
    const wtn = window.WTN;
    if (!wtn) return false;
    try {
      if (typeof wtn.isAndroidApp === 'function' && wtn.isAndroidApp()) return true;
      if (typeof wtn.isIosApp === 'function' && wtn.isIosApp()) return true;
      if (typeof wtn.isAndroidORIosApp === 'function' && wtn.isAndroidORIosApp()) return true;
    } catch (e) {
      return false;
    }
    return false;
  })();
  // The Google app's in-app browser (UA contains "GSA/") is a normal browser, not our app.
  const isGoogleAppBrowser = typeof navigator !== 'undefined' && /GSA\//.test(navigator.userAgent || '');
  const isNativeWebView = isAndroidWebView && !isInAppBrowser && !isGoogleAppBrowser;
  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
  const hasUtmSource = searchParams.get('utm_source') === 'google_play';
  const isInstalledApp = (isWtnApp || isNativeWebView || isStandalone || hasUtmSource) && !isGoogleAppBrowser;

  // Diagnostic log — fires once per page load to help trace detection issues
  React.useEffect(() => {
    console.log('[BODYMAX_LANDING]', {
      isWtnApp,
      wtnPresent: typeof window !== 'undefined' && !!window.WTN,
      isGoogleAppBrowser,
      isNativeWebView,
      isAndroidWebView,
      isInAppBrowser,
      isStandalone,
      hasUtmSource,
      isInstalledApp,
      landing: isInstalledApp ? 'AppWelcome' : 'Landing',
      viewport: typeof window !== 'undefined' ? window.innerWidth : null,
      hasUser: !!user,
      ua: navigator.userAgent?.slice(0, 120),
      referrer: document.referrer,
    });
  }, []);

  // A stored token means a session is being restored (app resume / reload).
  // Wait for /auth/me to resolve instead of flashing the sign-up landing.
  if (token && loading && !user) return <LoadingScreen />;
  if (user) return <Navigate to={user.onboarding_complete ? '/dashboard' : '/onboarding'} replace />;

  if (isInstalledApp) {
    return <Suspense fallback={<LoadingScreen />}><AppWelcome /></Suspense>;
  }
  return <Landing />;
}

function PageTracker() {
  usePageTracking();
  return null;
}

/**
 * Consumes a bridge_token from the URL (webview → browser session transfer).
 * Runs once on mount. If a bridge_token is present, exchanges it for a full session.
 */
function BridgeTokenConsumer() {
  const { login: _unused, ...auth } = useAuth();
  const location = useLocation();

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const bridgeToken = params.get('bridge_token');
    if (!bridgeToken) return;

    // Clean the token from URL immediately
    const cleanURL = window.location.pathname;
    window.history.replaceState({}, '', cleanURL);

    const API = process.env.REACT_APP_BACKEND_URL;
    axios.post(`${API}/api/auth/consume-bridge`, { bridge_token: bridgeToken })
      .then(res => {
        if (res.data.token) {
          // Persist token using all 3 stores
          localStorage.setItem('bodymax-token', res.data.token);
          sessionStorage.setItem('bodymax-token', res.data.token);
          const secure = window.location.protocol === 'https:' ? ';Secure' : '';
          document.cookie = `bm_token=${res.data.token};path=/;max-age=${7*86400};SameSite=Lax${secure}`;
          // Force page reload to re-bootstrap AuthContext with the new token
          window.location.href = cleanURL;
        }
      })
      .catch(() => {
        // Bridge failed — user will see login page normally
      });
  }, []);

  return null;
}

// Prefetch core lazy pages after initial render
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    setTimeout(() => {
      import("@/pages/Chat");
      import("@/pages/Achievements");
      import("@/pages/Settings");
    }, 2000);
    setTimeout(() => {
      import("@/pages/ActiveWorkout");
      import("@/pages/Subscription");
    }, 4000);
  }, { once: true });
}




function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading && !user) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" />;
  return children;
}

function OnboardingGate({ children }) {
  const { user, loading } = useAuth();
  if (loading && !user) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" />;
  if (!user.onboarding_complete) return <Navigate to="/onboarding" />;
  return children;
}

// Performs a reload deferred by the version check once the user leaves the
// active workout / onboarding screens.
function VersionReloadOnNavigate() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    notifyRouteChange(pathname);
  }, [pathname]);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/**
 * Android hardware back button handler.
 * Navigates back in React Router history. If on a main tab (dashboard),
 * minimizes the app instead of navigating to the landing page.
 */
function AndroidBackHandler() {
  const location = useLocation();
  
  React.useEffect(() => {
    let cleanup = null;
    
    const setupBackHandler = async () => {
      try {
        const { App: CapApp } = await import('@capacitor/app');
        const listener = await CapApp.addListener('backButton', ({ canGoBack }) => {
          const mainTabs = ['/dashboard', '/workout', '/chat', '/achievements', '/settings'];
          const isMainTab = mainTabs.includes(location.pathname);
          
          if (isMainTab) {
            // On a main tab — minimize app (don't navigate away)
            CapApp.minimizeApp();
          } else if (canGoBack && window.history.length > 1) {
            window.history.back();
          } else {
            CapApp.minimizeApp();
          }
        });
        cleanup = () => listener.remove();
      } catch (e) {
        // Not running in Capacitor — no-op for browser
      }
    };
    
    setupBackHandler();
    return () => { if (cleanup) cleanup(); };
  }, [location.pathname]);
  
  return null;
}

function SubscriptionGate({ children }) {
  const { user, loading, subscriptionStatus, checkSubscription } = useAuth();
  const [checking, setChecking] = React.useState(true);
  
  React.useEffect(() => {
    const check = async () => {
      if (user && user.onboarding_complete) {
        await checkSubscription();
      }
      setChecking(false);
    };
    check();
  }, [user]);
  
  if ((loading || checking) && !user) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" />;
  if (!user.onboarding_complete) return <Navigate to="/onboarding" />;
  
  // REMOVED: Old subscription gate - now using in-page paywall (Day 1 free, Day 2+ locked)
  // Users can access workout page, but Day 2+ start buttons are locked until subscription
  
  return children;
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-muted-foreground font-heading">Loading...</p>
      </div>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  // CRITICAL: Detect session_id synchronously during render, BEFORE useEffect/AuthProvider
  // This prevents race conditions where auth checks run before callback processes
  if (location.hash?.includes('session_id=')) {
    return <GoogleCallback />;
  }
  return (
    <Suspense fallback={<LoadingScreen />}>
    <Routes>
      <Route path="/" element={<HomeRoute />} />
      <Route path="/login" element={<Auth mode="login" />} />
      <Route path="/register" element={<Auth mode="register" />} />
      <Route path="/auth/google/callback" element={<GoogleCallback />} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/confirm-email" element={<ConfirmEmailChange />} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/subscribe" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
      <Route path="/subscription/cancel" element={<ProtectedRoute><SubscriptionCancel /></ProtectedRoute>} />
      <Route path="/dashboard" element={<SubscriptionGate><Dashboard /></SubscriptionGate>} />
      <Route path="/workout" element={<SubscriptionGate><Workout /></SubscriptionGate>} />
      <Route path="/workout/:id" element={<SubscriptionGate><Workout /></SubscriptionGate>} />
      <Route path="/workout/active/:planId/:dayNumber" element={<SubscriptionGate><ActiveWorkout /></SubscriptionGate>} />
      <Route path="/custom-builder" element={<ProtectedRoute><CustomProgramBuilder /></ProtectedRoute>} />
      <Route path="/chat" element={<SubscriptionGate><Chat /></SubscriptionGate>} />
      <Route path="/analytics" element={<SubscriptionGate><Analytics /></SubscriptionGate>} />
      <Route path="/achievements" element={<SubscriptionGate><Achievements /></SubscriptionGate>} />
      <Route path="/exercises" element={<SubscriptionGate><ExerciseLibrary /></SubscriptionGate>} />
      <Route path="/progression" element={<SubscriptionGate><Progression /></SubscriptionGate>} />
      <Route path="/settings" element={<SubscriptionGate><Settings /></SubscriptionGate>} />
      <Route path="/dev/exercises" element={<ProtectedRoute><DevExerciseGallery /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/admin/exercise-audit" element={<ProtectedRoute><ExerciseAudit /></ProtectedRoute>} />
      <Route path="/faqs" element={<FAQs />} />
      <Route path="/support" element={<Support />} />
      {/* Public blog — no auth required */}
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogArticle />} />
      {/* Legal pages - accessible without login for store compliance */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/legal/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/legal/terms" element={<TermsOfService />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route path="/legal/delete-account" element={<DeleteAccount />} />
      <Route path="/account-deletion" element={<AccountDeletionRedirect />} />
      <Route path="/download" element={<SimpleLanding mode="download" />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Helmet>
        <title>BODYMAX | Personal Trainer App — Built Around You</title>
      </Helmet>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter unstable_useTransitions={false}>
            <ScrollToTop />
            <VersionReloadOnNavigate />
            <PageTracker />
            <BridgeTokenConsumer />
            <AndroidBackHandler />
            <InstallProvider>
            <EngineProvider>
              <AppLayout>
                <ErrorBoundary>
                  <AppRoutes />
                </ErrorBoundary>
              </AppLayout>
              <InAppBrowserBanner />
              <CookieConsent />
            </EngineProvider>
            </InstallProvider>
          </BrowserRouter>
          <Toaster />
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
